/* sprint-gallery.js — flavor-specific gallery filter v5.1
 *
 * Shows exactly 3 images when a flavor is selected on the Squeeze Pouch page:
 *   1. {Flavor} — Front
 *   2. {Flavor} — Back
 *   3. MMA Fighter with Product_RasLem (shared across all flavors)
 *
 * Strategy C  — variant changes: intercept Horizon's 'variant:update' event
 *               in the capture phase and trim before media-gallery processes it.
 * Strategy B  — initial page load (no ?variant= param): hide the gallery,
 *               programmatically click the Raspberry Finish radio so Horizon
 *               runs its own variant-change flow (which Strategy C then trims),
 *               then restore visibility.
 */
(function () {
  'use strict';

  var MMA_ALT         = 'MMA fighter fueling with Raspberry Lemon Pouch';
  var FRONT_MARK      = ' — Front';
  var BACK_MARK       = ' — Back';
  var TRIMMED         = 'data-sprint-trimmed';
  var EVENT_NAME      = 'variant:update';
  var DEFAULT_FLAVOR  = 'Raspberry Finish';
  var DEFAULT_VARIANT = '48881586209019';  // Raspberry Finish 6-Pack

  /* ── helpers ────────────────────────────────────────────────────────── */

  function imgAlt(el) {
    var img = el && el.querySelector('img');
    return img ? (img.getAttribute('alt') || '') : '';
  }

  function isFlavorGallery(gallery) {
    return !!gallery.querySelector('slideshow-slide img[alt*="— Front"]');
  }

  function getSelectedFlavor() {
    var input = document.querySelector(
      'variant-picker fieldset[data-fieldset-index="0"] input[data-current-checked="true"]'
    );
    return input ? input.value : null;
  }

  /* ── core gallery trim ─────────────────────────────────────────────── */

  /* Returns true if trimming actually happened, false otherwise.
   * Safe to call on both detached (pre-connect) and live elements. */
  function trimGallery(gallery, flavor) {
    if (!gallery || gallery.hasAttribute(TRIMMED)) return false;
    if (!isFlavorGallery(gallery)) return false;

    gallery.setAttribute(TRIMMED, '');

    var frontAlt = flavor + FRONT_MARK;
    var backAlt  = flavor + BACK_MARK;

    var slides = Array.from(gallery.querySelectorAll('slideshow-slide'));
    if (!slides.length) return false;

    var frontSlide = null, backSlide = null, mmaSlide = null;
    slides.forEach(function (s) {
      var a = imgAlt(s);
      if (!frontSlide && a === frontAlt) frontSlide = s;
      if (!backSlide  && a === backAlt)  backSlide  = s;
      if (!mmaSlide   && a === MMA_ALT)  mmaSlide   = s;
    });

    if (!frontSlide) return false;

    var ordered  = [frontSlide, backSlide, mmaSlide].filter(Boolean);
    var keepSet  = new Set(ordered);
    var keepAlts = new Set(ordered.map(imgAlt));

    slides.forEach(function (s) { if (!keepSet.has(s)) s.remove(); });

    var sc = frontSlide.parentNode;
    if (sc) ordered.forEach(function (el) { sc.appendChild(el); });

    var thumbContainer = gallery.querySelector(
      'slideshow-controls .slideshow-controls__thumbnails'
    );
    if (thumbContainer) {
      var altToThumb = {};
      Array.from(
        gallery.querySelectorAll('slideshow-controls .slideshow-controls__thumbnail')
      ).forEach(function (btn) {
        var img = btn.querySelector('img');
        var a   = img ? (img.getAttribute('alt') || '') : '';
        if (keepAlts.has(a)) {
          altToThumb[a] = btn;
        } else {
          btn.remove();
        }
      });

      ordered.forEach(function (slide, newIdx) {
        var a   = imgAlt(slide);
        var btn = altToThumb[a];
        if (!btn) return;
        var attr = btn.getAttribute('on:click') || '';
        btn.setAttribute('on:click', attr.replace(/\/select\/\d+$/, '/select/' + newIdx));
        thumbContainer.appendChild(btn);
      });
    }

    gallery.querySelectorAll('.media-gallery__grid > li').forEach(function (li) {
      if (!keepAlts.has(imgAlt(li))) li.remove();
    });

    return true;
  }

  /* ── Strategy C: intercept variant:update event (capture phase) ─────── */
  document.addEventListener(EVENT_NAME, function (event) {
    var htmlDoc = event.detail && event.detail.data && event.detail.data.html;
    if (!htmlDoc) return;

    var mg = typeof htmlDoc.querySelector === 'function'
           ? htmlDoc.querySelector('media-gallery')
           : null;
    if (!mg || !isFlavorGallery(mg)) return;

    var flavor = getSelectedFlavor();
    if (!flavor) return;

    trimGallery(mg, flavor);
  }, true);

  /* ── Strategy B: initial page load ───────────────────────────────── */
  function onLoad() {
    var gallery = document.querySelector('media-gallery');
    if (!gallery || !isFlavorGallery(gallery)) return;

    // If a variant is already in the URL the server rendered the right one;
    // Strategy C will handle any subsequent user-driven changes.
    var urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('variant')) return;

    // No variant param — find the Raspberry Finish radio
    var rfInput = document.querySelector(
      'variant-picker fieldset[data-fieldset-index="0"] input[value="' + DEFAULT_FLAVOR + '"]'
    );
    if (!rfInput) return;

    // Already on Raspberry Finish? Fall back to the fetch-and-replace path.
    if (rfInput.getAttribute('data-current-checked') === 'true' || rfInput.checked) {
      fetchAndReplace(gallery, DEFAULT_FLAVOR, DEFAULT_VARIANT);
      return;
    }

    // Hide the gallery so the user never sees the wrong flavor
    gallery.style.visibility = 'hidden';

    // Restore visibility once variant:update fires (Strategy C will have trimmed
    // the new gallery by then)
    document.addEventListener(EVENT_NAME, function restoreVis() {
      document.removeEventListener(EVENT_NAME, restoreVis, true);
      setTimeout(function () {
        var g = document.querySelector('media-gallery');
        if (g) g.style.visibility = '';
      }, 80);
    }, true);

    // Safety net: restore visibility after 3 s in case variant:update never fires
    setTimeout(function () {
      var g = document.querySelector('media-gallery');
      if (g) g.style.visibility = '';
    }, 3000);

    // Trigger Horizon’s own variant-change flow by clicking the radio.
    // Use whenDefined to ensure the custom element is initialized first.
    var doClick = function () {
      requestAnimationFrame(function () { rfInput.click(); });
    };
    if (customElements.get('variant-picker')) {
      doClick();
    } else {
      customElements.whenDefined('variant-picker').then(doClick);
    }
  }

  /* ── fetch-and-replace (fallback when already on Raspberry Finish) ──── */
  function fetchAndReplace(gallery, flavor, variantId) {
    var sectionEl = gallery.closest('[id^="shopify-section-"]');
    if (!sectionEl) return;
    var sectionId = sectionEl.id.replace('shopify-section-', '');

    var url = window.location.pathname.split('?')[0] +
              '?sections=' + encodeURIComponent(sectionId) +
              '&variant='  + encodeURIComponent(variantId);

    var superseded = false;
    document.addEventListener(EVENT_NAME, function () { superseded = true; }, { once: true });

    window.fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (superseded) return;
        var html = data[sectionId];
        if (!html) return;
        var wrap = document.createElement('div');
        wrap.innerHTML = html;
        var newGallery = wrap.querySelector('media-gallery');
        if (!newGallery) return;
        var currentFlavor = getSelectedFlavor() || flavor;
        var trimmed = trimGallery(newGallery, currentFlavor);
        if (!trimmed) return;  // don’t replace if trimming failed
        var current = document.querySelector('media-gallery');
        if (current && current.parentNode) {
          current.parentNode.replaceChild(newGallery, current);
        }
      })
      .catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onLoad);
  } else {
    onLoad();
  }

})();
